import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Mail, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { AuthShell } from "./AuthShell";
import { useAuth, roleHome } from "../../context/AuthContext";
import CountryPicker from "../../components/CountryPicker";
import { findByIso, DEFAULT_ISO } from "../../lib/countries";
import api from "../../lib/api";

// Passwordless auth screen — Continue with Phone (WhatsApp OTP via AiSensy) or
// Continue with Email (OTP via existing Resend integration).
// If the OTP resolves to an existing user we drop them at their role home.
// Otherwise we collect just the missing profile fields and create the account.

const RESEND_START = 60;

export default function Login() {
  const {
    requestPhoneOtp, verifyPhoneOtp, completePhoneSignup,
    requestEmailOtp, verifyEmailOtp, completeEmailSignup,
  } = useAuth();
  const navigate = useNavigate();

  const [stage, setStage] = useState("choose"); // choose | phone-input | phone-otp | phone-signup | email-input | email-otp | email-signup
  const [busy, setBusy] = useState(false);
  const [countryIso, setCountryIso] = useState(DEFAULT_ISO);   // country selector state (ISO alpha-2)
  const [phoneNumber, setPhoneNumber] = useState("");          // local digits only, no country code
  const [phoneNormalized, setPhoneNormalized] = useState("");  // full E.164 returned by backend
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [signupToken, setSignupToken] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [signup, setSignup] = useState({ first_name: "", last_name: "", email: "", current_equity: "", broker: "" });

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  // Non-blocking default-country detection via our own backend (edge country
  // header). Failure is silent — the picker stays on the product default.
  useEffect(() => {
    let cancelled = false;
    api.get("/geo/country", { timeout: 3500 })
      .then(({ data }) => {
        if (cancelled) return;
        const hit = findByIso(data.country);
        if (hit) setCountryIso(hit.iso);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const goHome = (user) => navigate(roleHome(user.role) || "/investor");

  // Compose the international number the backend expects: +<cc><local>.
  // If the user pasted a full international number into the local field,
  // strip the duplicated country code so we never emit "+9191…".
  const composePhone = () => {
    const c = findByIso(countryIso);
    const ccDigits = (c?.dial || "+91").replace(/\D/g, "");
    let digits = (phoneNumber || "").replace(/\D/g, "");
    if (digits.startsWith("00" + ccDigits) && digits.length - ccDigits.length - 2 >= 8) {
      digits = digits.slice(ccDigits.length + 2);
    } else if (digits.startsWith(ccDigits) && digits.length - ccDigits.length >= 8) {
      digits = digits.slice(ccDigits.length);
    }
    digits = digits.replace(/^0+/, "");
    return `+${ccDigits}${digits}`;
  };

  // ─── Phone: request ────────────────────────────────────────────────────
  const submitPhone = async (e) => {
    e.preventDefault();
    const full = composePhone();
    if (!full || full.replace(/\D/g, "").length < 9) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setBusy(true);
    try {
      const data = await requestPhoneOtp(full);
      setPhoneNormalized(data.phone);
      setResendIn(data.resend_in || RESEND_START);
      setStage("phone-otp");
      toast.success("WhatsApp code sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to send verification code. Please try again.");
    } finally { setBusy(false); }
  };

  const resendPhone = async () => {
    if (resendIn > 0) return;
    setBusy(true);
    try {
      const data = await requestPhoneOtp(phoneNormalized || composePhone());
      setResendIn(data.resend_in || RESEND_START);
      toast.success("New code sent to WhatsApp");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend code");
    } finally { setBusy(false); }
  };

  const submitPhoneOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await verifyPhoneOtp(phoneNormalized || composePhone(), code);
      if (data.new_user) {
        setSignupToken(data.signup_token);
        setStage("phone-signup");
      } else {
        toast.success(`Welcome back, ${data.user.name.split(" ")[0]}`);
        goHome(data.user);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const submitPhoneSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await completePhoneSignup({
        phone: phoneNormalized || composePhone(),
        signup_token: signupToken,
        first_name: signup.first_name.trim(),
        last_name: signup.last_name.trim(),
        email: signup.email.trim(),
        current_equity: parseFloat(signup.current_equity) || 0,
        broker: signup.broker.trim(),
      });
      toast.success("Account created");
      goHome(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  // ─── Email flow ────────────────────────────────────────────────────────
  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await requestEmailOtp(email);
      setResendIn(data.resend_in || RESEND_START);
      setStage("email-otp");
      toast.success("Verification code sent to your email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const resendEmail = async () => {
    if (resendIn > 0) return;
    setBusy(true);
    try {
      const data = await requestEmailOtp(email);
      setResendIn(data.resend_in || RESEND_START);
      toast.success("New code sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend code");
    } finally { setBusy(false); }
  };

  const submitEmailOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await verifyEmailOtp(email, code);
      if (data.new_user) {
        setSignupToken(data.signup_token);
        setStage("email-signup");
      } else {
        toast.success(`Welcome back, ${data.user.name.split(" ")[0]}`);
        goHome(data.user);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const submitEmailSignup = async (e) => {
    e.preventDefault();
    const fullPhone = composePhone();
    if (!fullPhone || fullPhone.replace(/\D/g, "").length < 9) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setBusy(true);
    try {
      const data = await completeEmailSignup({
        email,
        signup_token: signupToken,
        first_name: signup.first_name.trim(),
        last_name: signup.last_name.trim(),
        phone: fullPhone,
        country: findByIso(countryIso)?.name || null,
        current_equity: parseFloat(signup.current_equity) || 0,
        broker: signup.broker.trim(),
      });
      toast.success("Account created");
      goHome(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  const restart = () => { setStage("choose"); setCode(""); setSignupToken(""); setResendIn(0); };

  // ─── Renderers ─────────────────────────────────────────────────────────
  if (stage === "choose") return (
    <AuthShell title="Welcome to ChartzFx" subtitle="Securely access your trading control room">
      <div className="space-y-3" data-testid="auth-chooser">
        {/* Phone OTP temporarily disabled — email verification is the only new-user path.
            To re-enable, restore the "auth-continue-phone" button below. */}
        <button data-testid="auth-continue-email" onClick={() => setStage("email-input")}
                className="w-full py-3.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#7000FF]/40 transition-all flex items-center gap-3 px-4 group">
          <span className="w-9 h-9 rounded-full bg-[#00F0FF]/15 text-[#00F0FF] flex items-center justify-center"><Mail size={16} /></span>
          <div className="flex-1 text-left">
            <p className="text-white text-sm">Continue with Email</p>
            <p className="text-[11px] text-gray-500">Verification code by email</p>
          </div>
          <span className="text-gray-500 group-hover:text-white transition">→</span>
        </button>
      </div>
      <p className="text-[11px] text-gray-600 text-center mt-6 leading-relaxed">
        By continuing you agree to ChartzFx's <a href="/terms" className="text-[#00F0FF] hover:underline">Terms</a> and <a href="/privacy" className="text-[#00F0FF] hover:underline">Privacy Policy</a>.
      </p>
    </AuthShell>
  );

  if (stage === "phone-input") {
    const sel = findByIso(countryIso);
    return (
    <AuthShell title="Continue with Phone" subtitle="We'll send a one-time code on WhatsApp">
      <form onSubmit={submitPhone} className="space-y-4" data-testid="phone-input-form">
        <div>
          <label className="label-xs block mb-2">Phone Number</label>
          <div className="flex" data-testid="phone-combined-input">
            <CountryPicker value={countryIso} onChange={setCountryIso} disabled={busy} />
            <input data-testid="phone-input" type="tel" inputMode="numeric" required autoFocus
                   className="input-dark flex-1 min-w-0 !rounded-l-none !border-l-0" placeholder="98765 43210"
                   value={phoneNumber}
                   onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s()-]/g, ""))} />
          </div>
          <p className="text-[11px] text-gray-600 mt-2">
            Sending to <span className="text-gray-400 tabular">{sel ? `${sel.dial}` : "+91"}{phoneNumber ? " " + phoneNumber : ""}</span> — change the country code if needed.
          </p>
        </div>
        <button disabled={busy} data-testid="phone-send-otp" className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Smartphone size={14} />} {busy ? "Sending…" : "Send WhatsApp Code"}
        </button>
      </form>
      <button onClick={restart} data-testid="back-to-chooser" className="text-xs text-gray-500 hover:text-white mt-5 flex items-center gap-1 mx-auto">
        <ArrowLeft size={11} /> Use a different method
      </button>
    </AuthShell>
    );
  }

  if (stage === "phone-otp") return (
    <AuthShell title="Enter Verification Code" subtitle={`We sent a 6-digit code on WhatsApp to ${phoneNormalized || composePhone()}`}>
      <form onSubmit={submitPhoneOtp} className="space-y-4" data-testid="phone-otp-form">
        <input data-testid="phone-otp-input" type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} required autoFocus
               className="input-dark !text-center !text-2xl !tracking-[0.5em] !py-3 tabular"
               value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
        <button disabled={busy || code.length !== 6} data-testid="phone-verify-btn" className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} {busy ? "Verifying…" : "Verify & Continue"}
        </button>
        <div className="flex items-center justify-between text-xs">
          <button type="button" onClick={() => setStage("phone-input")} className="text-gray-500 hover:text-white flex items-center gap-1"><ArrowLeft size={10} /> Edit phone</button>
          <button type="button" onClick={resendPhone} disabled={resendIn > 0 || busy}
                  data-testid="phone-resend-btn"
                  className="text-[#00F0FF] hover:underline disabled:text-gray-600 disabled:no-underline">
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
        </div>
      </form>
    </AuthShell>
  );

  if (stage === "phone-signup") return (
    <AuthShell title="Complete your profile" subtitle="Just a few details and you're in">
      <form onSubmit={submitPhoneSignup} className="space-y-3" data-testid="phone-signup-form">
        <p className="text-[11px] text-[#00E676] bg-[#00E676]/10 border border-[#00E676]/30 rounded px-3 py-1.5">
          ✓ Verified {phoneNormalized || composePhone()}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="signup-first-name" required placeholder="First name" className="input-dark"
                 value={signup.first_name} onChange={(e) => setSignup({ ...signup, first_name: e.target.value })} />
          <input data-testid="signup-last-name" required placeholder="Last name" className="input-dark"
                 value={signup.last_name} onChange={(e) => setSignup({ ...signup, last_name: e.target.value })} />
        </div>
        <input data-testid="signup-email" type="email" required placeholder="Email address" className="input-dark"
               value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} />
        <input data-testid="signup-equity" type="number" step="0.01" min="0" required placeholder="Current trading equity (USD)" className="input-dark"
               value={signup.current_equity} onChange={(e) => setSignup({ ...signup, current_equity: e.target.value })} />
        <input data-testid="signup-broker" required placeholder="Broker (e.g. Exness, XM, IC Markets)" className="input-dark"
               value={signup.broker} onChange={(e) => setSignup({ ...signup, broker: e.target.value })} />
        <button disabled={busy} data-testid="signup-submit" className="btn-primary w-full py-2.5 text-sm disabled:opacity-50">
          {busy ? "Creating account…" : "Create Account & Continue"}
        </button>
      </form>
    </AuthShell>
  );

  if (stage === "email-input") return (
    <AuthShell title="Continue with Email" subtitle="We'll send a one-time code to your inbox">
      <form onSubmit={submitEmail} className="space-y-4" data-testid="email-input-form">
        <div>
          <label className="label-xs block mb-2">Email Address</label>
          <div className="relative">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input data-testid="email-input" type="email" required autoFocus
                   className="input-dark !pl-9" placeholder="you@email.com"
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
        <button disabled={busy} data-testid="email-send-otp" className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} {busy ? "Sending…" : "Send Email Code"}
        </button>
      </form>
      <button onClick={restart} data-testid="back-to-chooser-2" className="text-xs text-gray-500 hover:text-white mt-5 flex items-center gap-1 mx-auto">
        <ArrowLeft size={11} /> Use a different method
      </button>
    </AuthShell>
  );

  if (stage === "email-otp") return (
    <AuthShell title="Enter Verification Code" subtitle={`We sent a 6-digit code to ${email}`}>
      <form onSubmit={submitEmailOtp} className="space-y-4" data-testid="email-otp-form">
        <input data-testid="email-otp-input" type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} required autoFocus
               className="input-dark !text-center !text-2xl !tracking-[0.5em] !py-3 tabular"
               value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
        <button disabled={busy || code.length !== 6} data-testid="email-verify-btn" className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} {busy ? "Verifying…" : "Verify & Continue"}
        </button>
        <div className="flex items-center justify-between text-xs">
          <button type="button" onClick={() => setStage("email-input")} className="text-gray-500 hover:text-white flex items-center gap-1"><ArrowLeft size={10} /> Edit email</button>
          <button type="button" onClick={resendEmail} disabled={resendIn > 0 || busy}
                  data-testid="email-resend-btn"
                  className="text-[#00F0FF] hover:underline disabled:text-gray-600 disabled:no-underline">
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
        </div>
      </form>
    </AuthShell>
  );

  if (stage === "email-signup") return (
    <AuthShell title="Complete your profile" subtitle="Just a few details and you're in">
      <form onSubmit={submitEmailSignup} className="space-y-3" data-testid="email-signup-form">
        <p className="text-[11px] text-[#00E676] bg-[#00E676]/10 border border-[#00E676]/30 rounded px-3 py-1.5">
          ✓ Verified {email}
        </p>
        <div>
          <label className="label-xs block mb-1.5">Phone Number</label>
          <div className="flex" data-testid="signup-phone-combined">
            <CountryPicker value={countryIso} onChange={setCountryIso} disabled={busy} />
            <input data-testid="signup-phone-e" type="tel" inputMode="numeric" required
                   className="input-dark flex-1 min-w-0 !rounded-l-none !border-l-0" placeholder="Phone number"
                   value={phoneNumber}
                   onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s()-]/g, ""))} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="signup-first-name-e" required placeholder="First name" className="input-dark"
                 value={signup.first_name} onChange={(e) => setSignup({ ...signup, first_name: e.target.value })} />
          <input data-testid="signup-last-name-e" required placeholder="Last name" className="input-dark"
                 value={signup.last_name} onChange={(e) => setSignup({ ...signup, last_name: e.target.value })} />
        </div>
        <input data-testid="signup-equity-e" type="number" step="0.01" min="0" required placeholder="Current trading equity (USD)" className="input-dark"
               value={signup.current_equity} onChange={(e) => setSignup({ ...signup, current_equity: e.target.value })} />
        <input data-testid="signup-broker-e" required placeholder="Broker (e.g. Exness, XM, IC Markets)" className="input-dark"
               value={signup.broker} onChange={(e) => setSignup({ ...signup, broker: e.target.value })} />
        <button disabled={busy} data-testid="signup-submit-e" className="btn-primary w-full py-2.5 text-sm disabled:opacity-50">
          {busy ? "Creating account…" : "Create Account & Continue"}
        </button>
      </form>
    </AuthShell>
  );

  return null;
}
